'use client'

import React, { memo, useId, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Review } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'

import { getOptionModerationReasonLabel } from '@/features/property/option.moderation'
import { useReviewDecision } from '@/hooks'
import { getReviewMetadataValue, hasReviewMessages } from '@/utils/reviews'

import { MessageBox } from '../Box'
import { Divider } from '../..'

type MessagesListProps = {
  review?: Review
  onAddComment?: () => void
}

// Memoized MessageBox to prevent unnecessary re-renders
const MemoizedMessageBox = memo(
  ({
    review,
    message,
    isFirst,
    hideDivider,
  }: {
    review: Review
    message: any
    isFirst: boolean
    hideDivider?: boolean
  }) => (
    <MessageBox
      key={message?.id}
      review={review}
      message={message}
      isFirst={isFirst}
      hideDivider={hideDivider}
    />
  ),
)

MemoizedMessageBox.displayName = 'MemoizedMessageBox'

export const MessagesList = memo(
  ({ review, onAddComment }: MessagesListProps) => {
    const { messages: { items: messages = [] } = { items: [] } } = review || {}

    const t = useTranslations()
    const { applyCreationReviewDecision, getReviewDecisionAvailability } =
      useReviewDecision()

    const [showMore, setShowMore] = useState(false)
    const [isSubmittingDecision, setIsSubmittingDecision] = useState(false)
    const hiddenRepliesId = useId()

    const hasStartedReview = Boolean(review && hasReviewMessages(review))
    const handleShowMore = () => setShowMore(true)
    const isPendingEmptyReview =
      review?.status === ReviewStatus.PENDING && !hasStartedReview
    const isOptionCreationReview =
      review?.type === ReviewType.REVIEW_OPTION &&
      getReviewMetadataValue(review, 'isCreationReview') === 'true'
    const canRejectFromGenericThread = !isOptionCreationReview
    const decisionAvailability = getReviewDecisionAvailability(review)
    const decisionReasonLabel = getOptionModerationReasonLabel(
      t,
      decisionAvailability.reasonCode === 'admin-required'
        ? undefined
        : decisionAvailability.reasonCode,
    )

    const handleDecision = async (nextStatus: ReviewStatus) => {
      if (!review || isSubmittingDecision) return

      setIsSubmittingDecision(true)
      try {
        await applyCreationReviewDecision({ review, nextStatus })
      } catch {
        // Mutations report the failure; a rejected decision keeps the review open.
      } finally {
        setIsSubmittingDecision(false)
      }
    }

    const moreAnswersCount = messages.length - 2 > 0 ? messages.length - 2 : 1
    const lastMessage = messages[messages.length - 1]

    if (!review) return null

    if (!hasStartedReview) {
      if (!isPendingEmptyReview) return null

      return (
        <div className="flex flex-col gap-3 py-2">
          <div className="text-sm italic text-slate-400">
            {t('Review.missingInitialComment')}
          </div>
          {!decisionAvailability.canDecide && decisionReasonLabel ? (
            <div className="text-xs text-slate-400">{decisionReasonLabel}</div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleDecision(ReviewStatus.ACCEPTED)}
              disabled={!decisionAvailability.canDecide || isSubmittingDecision}
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={decisionReasonLabel ?? t('Common.accept')}
            >
              {t('Common.accept')}
            </button>
            {canRejectFromGenericThread ? (
              <button
                type="button"
                onClick={() => handleDecision(ReviewStatus.REJECTED)}
                disabled={
                  !decisionAvailability.canDecide || isSubmittingDecision
                }
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  decisionReasonLabel ?? t('Property.refuse-option-validation')
                }
              >
                {t('Property.refuse-option-validation')}
              </button>
            ) : null}
            {onAddComment && (
              <button
                type="button"
                onClick={onAddComment}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
              >
                {t('Review.addComment')}
              </button>
            )}
          </div>
        </div>
      )
    }

    return messages.length > 2 && !showMore ? (
      <div className="w-full">
        <MemoizedMessageBox
          review={review}
          message={messages?.[0]}
          isFirst
          hideDivider
        />
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 border-0 bg-transparent p-0 text-left hover:cursor-pointer"
          onClick={handleShowMore}
          aria-expanded={showMore}
          aria-controls={hiddenRepliesId}
        >
          <Divider />
          <span className="text-nowrap text-sm text-blue-600 hover:text-blue-800">
            {t('Review.moreAnswers', {
              count: moreAnswersCount,
            })}
          </span>
          <Divider />
        </button>
        <MemoizedMessageBox
          review={review}
          message={lastMessage}
          isFirst={false}
        />
      </div>
    ) : (
      <div id={hiddenRepliesId}>
        {messages?.map((message, index) => (
          <MemoizedMessageBox
            key={message.id}
            review={review}
            message={message}
            isFirst={index === 0}
          />
        ))}
      </div>
    )
  },
)

MessagesList.displayName = 'Messages'
